/* eslint-disable max-lines */
import React, { useEffect, useContext, useMemo } from 'react';
import { parse } from 'query-string';
import { useMediaQuery } from 'react-responsive';
import { useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import useGiftcardTracking from '../../Hooks/use-giftcard-tracking';
import useGiftcardChangeStep from '../../Hooks/use-giftcard-change-step';
import useLocalize from '../../Hooks/useLocalize';
import useLocale from '../../Hooks/use-locale';

import { ModalContainer } from '../../SubComponents/Modals';
import { Container, Content, Main, Sidebar } from '../../LayoutComponents/Layout';
import { Button } from '../../SubComponents/Buttons';
import { Row } from '../../SubComponents/Modals';
import Step from '../../ui-components/Step';
import ConfirmButton from '../../ui-components/confirm-button';
import { post } from '../../Utils/fetch';
import Footer from '../../ui-components/Footer';
import GiftcardStateProvider, {
  giftcardState,
} from './client-state/giftcard-state-context';
import PageMetadata from '../../metadata/page-metadata';
import { STAPE_GTM_ID } from '../../config';
import CustomGTMLoaderHead from '../../ui-components/custom-gtm-loader/head';
import CustomGTMLoaderBody from '../../ui-components/custom-gtm-loader/body';

// Data components
import HeaderWithData from './data-components/header-with-data';
import GiftcardFAQWithData from './data-components/giftcard-faq-with-data';
import LocationWithData from './data-components/location-with-data';
import GiftCardAmount from './data-components/gift-card-amount';
import CustomerFormWithData from './data-components/customer-form-with-data';
import RecipientFormWithData from './data-components/recipient-form-with-data';
import SummaryWithData from './data-components/summary-with-data';
import TermsWithData from './data-components/terms-with-data';
import NewsletterWithData from './data-components/newsletter-with-data';
import SummaryMobileWithData from './data-components/summary-mobile-with-data';
import PaymentModalWithData from './data-components/payment-modal-with-data';
import PaymentSuccessWithData from './data-components/payment-success-with-data';

// Actions
import { CurrentStepReducerActionTypes } from '../../redusers/current-step-reducer';
import { paymentReducerActionTypes } from '../../redusers/payment-reducer';
import { vouchersReducerActionTypes } from '../../redusers/vouchers-reducer';
import { errorReducerActionTypes } from '../../redusers/error-reducer';
import { loadingReducerActionTypes } from '../../redusers/loading-reducer';

// Server data
import useGiftcardMinimalShop from './server-data/use-giftcard-minimal-shop';
import useGiftcardShop from './server-data/use-giftcard-shop';

export const giftcardStepDependencies = {
  1: [],
  2: [
    {
      requirement: 'selectedShopId',
      errorType: errorReducerActionTypes.LOCATION_MISSING,
      errorMessage: 'Location is missing',
    },
  ],
  3: [
    {
      requirement: 'customer.formValid',
      errorType: errorReducerActionTypes.CUSTOMER_FORM,
      errorMessage: 'Customer form is not valid',
    },
  ],
};

const Giftcard = () => {
  const { data: minimalShop } = useGiftcardMinimalShop();
  const { data: shop } = useGiftcardShop();
  const { state, dispatch } = useContext(giftcardState);
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const localize = useLocalize();
  const { languageAsEnum } = useLocale();

  // Setup tracking, useTracking returns a 'track' function to track GTM and FB Pixel events
  const track = useGiftcardTracking();

  // Change step if conditions are meet
  const changeStep = useGiftcardChangeStep(giftcardStepDependencies);

  // -- Params --
  const location = useLocation();
  const params = useMemo(() => parse(location.search), [location.search]);

  // TODO: Is this the best way to do this? ↓
  useEffect(() => {
    if (params.duration && state.selectedShopId) {
      dispatch({ type: CurrentStepReducerActionTypes.CURRENT_STEP_UPDATE, payload: 2 });
    }
  }, [dispatch, params.duration, state.selectedShopId]);

  // TODO: Is this the best way to do this? ↓
  useEffect(() => {
    const applyVoucher = async (voucherCode: string) => {
      try {
        dispatch({ type: loadingReducerActionTypes.VOUCHERS, payload: true });
        const voucher = await post(`Giftcards/validate-voucher`, {
          voucherCode,
          shopId: state.selectedShopId,
        });

        return dispatch({
          type: vouchersReducerActionTypes.UPDATE_VOUCHERS,
          payload: [voucher],
        });
      } catch (error) {
        return console.log(error);
      }
    };

    if (
      params.voucherCode &&
      typeof params.voucherCode === 'string' &&
      state.selectedShopId
    ) {
      applyVoucher(params.voucherCode);
    }
  }, [dispatch, params.voucherCode, state.selectedShopId]);

  const usesNewGTMSetup = shop?.configuration?.gtmId === STAPE_GTM_ID;

  return (
    <>
      {usesNewGTMSetup ? (
        <>
          <CustomGTMLoaderHead domain={shop.websiteRoot.replace('https://', '')} />
          <CustomGTMLoaderBody domain={shop.websiteRoot.replace('https://', '')} />
        </>
      ) : null}
      <PageMetadata
        shopId={minimalShop?.id}
        shopName={minimalShop?.name}
        shopCountry={minimalShop?.country}
      >
        <link rel="canonical" href={`https://${window.location.host}/giftcard`} />
      </PageMetadata>

      <HeaderWithData />

      <Content>
        <Container isOpen={state.payment.showPaymentModal} mb="4" flexDirection="column">
          <GiftcardFAQWithData />
        </Container>

        <Container isOpen={state.payment.showPaymentModal}>
          <Main>
            <Step
              onClick={() => changeStep(1, { onlyBackward: true })}
              currentStep={state.currentStep === 1}
              heading={`1. ${
                shop?.giftCardLocalizations?.stepOneHeading?.[languageAsEnum] ||
                localize('product.configureGiftcard')
              }`}
            >
              <Row>
                <LocationWithData />
              </Row>

              <GiftCardAmount />

              <Button
                onClick={() => {
                  changeStep(2);
                  track({ type: 'PRODUCT_VIEW_GIFTCARD', product: state.giftcard });
                  track({ type: 'ADD_TO_CART_GIFTCARD', product: state.giftcard });
                }}
                alignSelf="flex-end"
                className={!minimalShop?.id && 'disabled'}
              >
                {localize('product.next')}
              </Button>
            </Step>

            <Step
              onClick={() => changeStep(2, { onlyBackward: true })}
              currentStep={state.currentStep === 2}
              heading={`2. ${
                shop?.giftCardLocalizations?.stepTwoHeading?.[languageAsEnum] ||
                localize('product.yourInformation')
              }`}
            >
              <CustomerFormWithData />
            </Step>

            <Step
              onClick={() => changeStep(3, { onlyBackward: true })}
              currentStep={state.currentStep === 3}
              heading={`3. ${
                shop?.giftCardLocalizations?.stepThreeHeading?.[languageAsEnum] ||
                localize('giftcard.recipientInformation')
              }`}
            >
              <RecipientFormWithData />
            </Step>
          </Main>

          {!isMobile && (
            <Sidebar>
              <SummaryWithData closeable={false} />

              <TermsWithData />

              <NewsletterWithData />

              <ConfirmButton
                dependencies={[
                  { key: 'customer.formValid', value: true },
                  { key: 'currentStep', value: 3 },
                  {
                    key: 'terms.termsChecked',
                    value: true,
                    errorType: errorReducerActionTypes.TERMS,
                    clickable: true,
                  },
                  {
                    key: 'terms.newsletterSubscribed',
                    value: [true, false],
                    errorType: errorReducerActionTypes.NEWSLETTER,
                    clickable: true,
                  },
                ]}
                onClick={() => {
                  dispatch({
                    type: paymentReducerActionTypes.UPDATE_SHOW_PAYMENT_MODAL,
                    payload: true,
                  });
                  track({ type: 'CHECKOUT_GIFTCARD', product: state.giftcard, step: 4 });
                }}
                onError={(errorType) => {
                  if (errorType) {
                    if (errorType === errorReducerActionTypes.TERMS) {
                      dispatch({ type: errorReducerActionTypes.TERMS, payload: 'true' });
                    }

                    if (errorType === errorReducerActionTypes.NEWSLETTER) {
                      dispatch({
                        type: errorReducerActionTypes.NEWSLETTER,
                        payload: 'true',
                      });
                    }
                  }
                }}
              >
                {`${localize('product.goToCheckout')}`}
              </ConfirmButton>
            </Sidebar>
          )}
        </Container>
      </Content>

      {minimalShop?.country !== 'Australia' && (
        <Footer isOpen={state.payment.showPaymentModal} />
      )}

      {isMobile && <SummaryMobileWithData />}

      <ModalContainer
        isOpen={state.payment.showPaymentModal}
        onBackgroundClick={() => {
          dispatch({
            type: paymentReducerActionTypes.UPDATE_SHOW_PAYMENT_MODAL,
            payload: false,
          });
        }}
        onEscapeKeydown={() => {
          dispatch({
            type: paymentReducerActionTypes.UPDATE_SHOW_PAYMENT_MODAL,
            payload: false,
          });
        }}
        allowScroll={true}
      >
        <Container>
          <Main>
            {!state.payment.isPaid ? (
              <PaymentModalWithData />
            ) : (
              <PaymentSuccessWithData />
            )}
          </Main>

          {!isMobile && (
            <Sidebar>
              <SummaryWithData closeable={!state.payment.isPaid} />
            </Sidebar>
          )}
        </Container>
      </ModalContainer>
    </>
  );
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity,
      cacheTime: Infinity,
    },
  },
});

const GiftcardWithState = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <GiftcardStateProvider>
        <Giftcard />
      </GiftcardStateProvider>
    </QueryClientProvider>
  );
};

export default GiftcardWithState;
